import { css } from '@emotion/react'
import { ActionCards } from 'components/actions/ActionCards'
import { Block } from 'components/Block'
import { DirectiveLPArticleSection } from 'components/directive/directiveLandingPage/modern/section/DirectiveLPArticleSection'
import { ModernDirectiveGuide } from 'components/directive/directiveLandingPage/modern/section/ModernDirectiveGuide'
import { WhyDirectiveSection } from 'components/directive/directiveLandingPage/modern/section/WhyDirectiveSection'
import { getDirectiveSpec } from 'components/directive/utils/directiveSpecs'
import {
  useCreateDirectiveLogic,
  useDirectiveLPActions,
} from 'components/directive/utils/directiveUtils'
import { DirectiveLPPageProps } from 'components/directive/utils/types'
import { GoodTrustReferences } from 'components/estatePlanning/landing/references/GoodTrustReferences'
import { EstatePlanningSignupSection } from 'components/estatePlanning/landing/signup/EstatePlanningSignupSection'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { ContentfulImage } from 'components/Image'
import { LandingPageHero } from 'components/landingPageHero/LandingPageHero'
import { LandingPageTextImagePerks } from 'components/landingPageTextImageSection/LandingPageTextImagePerks'
import { Layout } from 'components/layout/Layout'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { SecuritySection } from 'components/security/SecuritySection'
import { Spacer } from 'components/spacer/Spacer'
import { Stack } from 'components/Stack'
import { SupportSection } from 'components/supportSection/SupportSection'
import { TestimonialsSection } from 'components/testimonialsSection/TestimonialsSection'
import { TrustWillPerksSection } from 'components/trustWillPerksSection/TrustWillPerksSection'
import { Title_h3_default } from 'components/Typography'
import { describeSimpleDirectiveFlow } from 'logic/directive/describe'
import { Trans, useTranslation } from 'next-i18next'
import { useEffect, useMemo } from 'react'
import { mutateAnalyticsData } from 'utils/analytics'
import { useContentfulImages } from 'utils/contentful/ContentfulImagesContext'
import { isOneOf, numberRange, toLowerCase } from 'utils/general'
import { useResponsiveLocalization, useWindowSize } from 'utils/hooks'
import { mapKeysToTrans } from 'utils/i18n/mapKeysToTrans'
import { BREAKPOINTS, pxToRemUnit } from 'utils/styled'
import { faqTitleCss, SButton, SHeroImageContainer } from './ModernDirectiveLandingPage.styled'

const numberOfMediaMentionsToDisplay = 5

export const ModernDirectiveLandingPage = ({
  directiveType,
  mediaMentions,
  mentions,
  faq,
  estatePlanningPrice,
  articles,
}: DirectiveLPPageProps) => {
  const images = useContentfulImages()
  const { isXLarge } = useWindowSize()
  const spec = useMemo(() => getDirectiveSpec(directiveType), [directiveType])
  const { landingPageNamespace } = spec
  const { t } = useTranslation(landingPageNamespace)
  const { t: tCommon } = useTranslation('directive_lp_common')
  const responsiveLocalization = useResponsiveLocalization()

  useEffect(() => {
    const { analyticsDirectiveType } = spec

    mutateAnalyticsData({ page: analyticsDirectiveType })
  }, [spec])

  const analyticsPlace =
    directiveType === 'WILL' ? 'will_lp' : directiveType === 'TRUST' ? 'trust_lp' : undefined

  const { cta, create, isCreating } = useCreateDirectiveLogic(directiveType, {
    place: analyticsPlace,
  })

  const actions = useDirectiveLPActions(directiveType)

  const heroPerks = mapKeysToTrans(
    t,
    numberRange(4).map((i) => `${landingPageNamespace}.hero.perk_${i}` as const)
  )

  if (!isOneOf(directiveType, ['WILL', 'TRUST'])) return null

  const { getButtonTone, getHeroTone, getIconColors } =
    describeSimpleDirectiveFlow(directiveType).landingPage()

  return (
    <Layout
      header={<TopNavigationLandingPage variant="estate-planning" noSpacingBelow />}
      footer={<Footer variant="estate-planning" />}
    >
      <PageTitle title={t(`${landingPageNamespace}.meta_title`)} />
      <PageMeta
        image={spec.openGraphImage}
        description={t(`${landingPageNamespace}.meta_description`)}
      />

      <LandingPageHero
        css={{ paddingTop: '6rem' }}
        caption={t(`${landingPageNamespace}.hero.caption`)}
        title={t(`${landingPageNamespace}.hero.title`)}
        tone={getHeroTone()}
        blockWidth={pxToRemUnit(1380)}
        videoButton={toLowerCase(directiveType)}
        mobileVariant="modern"
        image={
          <SHeroImageContainer>
            <ContentfulImage {...images[`${toLowerCase(directiveType)}_hero_modern`]} />
          </SHeroImageContainer>
        }
        overflowing={!isXLarge}
      >
        <LandingPageTextImagePerks iconColor={getIconColors().iconColor} perks={heroPerks} simple />
        <Spacer size={[1, 1.5]} />
        <SButton
          type="button"
          tone={getButtonTone()}
          size="large"
          loading={isCreating}
          onClick={create}
        >
          {cta}
        </SButton>
        <Spacer size={[1.5, 4]} />
        <GoodTrustReferences
          mediaMentions={mediaMentions}
          responsiveItemCounts={[
            numberOfMediaMentionsToDisplay,
            numberOfMediaMentionsToDisplay,
            numberOfMediaMentionsToDisplay,
          ]}
          caption={<Trans t={tCommon} i18nKey="directive_lp_common.hero.mentions_caption" />}
        />
      </LandingPageHero>

      <Spacer size={[3, 8]} />

      <Stack gap={['3rem', '8rem']}>
        <WhyDirectiveSection directiveType={directiveType} />

        <TrustWillPerksSection
          directiveType={directiveType}
          estatePlanningPrice={estatePlanningPrice}
        />
      </Stack>

      <Spacer size={[2, 3]} />

      <Stack gap={['3rem', '6rem']} accurateSpacers>
        <Block className="desktop-only">
          <ModernDirectiveGuide directiveType={directiveType} />
        </Block>

        <TestimonialsSection
          mediaMentions={mediaMentions}
          responsiveItemCounts={[3, 5, 5]}
          mentions={mentions}
        />

        <SecuritySection />

        <SupportSection />

        <DirectiveLPArticleSection directiveType={directiveType} articles={articles ?? []} />

        <Spacer size={[3, 2]} />

        <FaqTwoColumns
          content={faq}
          title={t(`${landingPageNamespace}.faq_title`)}
          titleVariant={Title_h3_default}
          titleCss={faqTitleCss}
        />

        <Block className="desktop-only">
          <ActionCards
            variant="round-button"
            title={tCommon('directive_lp_common.action_cards_title', responsiveLocalization)}
            actions={actions}
            scrollability="desktop-only"
            scrollerCss={css`
              padding-left: 0;
              padding-right: 0;
            `}
            css={css`
              @media ${BREAKPOINTS.MD.min} {
                grid-area: center / center / center / right;
              }
            `}
          />
        </Block>

        <EstatePlanningSignupSection
          directiveType={directiveType}
          mediaMentions={mediaMentions}
          place="will_lp"
        />
      </Stack>
    </Layout>
  )
}
